import React from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import MarkdownArticle from "./MarkdownArticle";
import { useGetBookQuery } from "../../services/rtkApi/agentApi";
import { useAppSelector } from "../../store/hooks";
import { isWriter } from "../authentication/RequireWriterRoute";
import { checkRole } from "../recipe_page/recipe_page";

/**
 * A single book at `/blog/books/:slug` — the description, then the chapters in
 * running order. Only published chapters appear; anything still in draft is
 * invisible here even to its author, who edits from `/blog/manage/books/:id`.
 */
const BookPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: book, isLoading, isError } = useGetBookQuery(slug || "", { skip: !slug });
  const auth = useAppSelector((state) => state.auth);

  const canEdit =
    isWriter(auth.role) &&
    (checkRole(auth.role ?? "", "admin") || book?.authorUsername === auth.username);

  if (isLoading) {
    return (
      <PageContainer>
        <Muted>loading book…</Muted>
      </PageContainer>
    );
  }

  if (isError || !book) {
    return (
      <PageContainer>
        <Muted>book not found</Muted>
        <Centered>
          <NavLink to="/blog/books">back to books</NavLink>
        </Centered>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Inner>
        <NavLink to="/blog/books">&larr; all books</NavLink>

        <BookHeader>
          <MetaRow>
            <KindTag>book</KindTag>
            {book.complete ? (
              <CompleteTag>complete</CompleteTag>
            ) : (
              <OngoingTag>ongoing</OngoingTag>
            )}
            <MetaText>
              {book.chapters.length}{" "}
              {book.chapters.length === 1 ? "chapter" : "chapters"}
            </MetaText>
            {book.authorUsername && <MetaText>· by @{book.authorUsername}</MetaText>}
          </MetaRow>
          <BookTitle>{book.title}</BookTitle>
          {book.subtitle && <BookSubtitle>{book.subtitle}</BookSubtitle>}
        </BookHeader>

        {book.coverImageUrl && <Cover src={book.coverImageUrl} alt="" />}

        {book.description?.trim() && (
          <DescriptionWrap>
            <MarkdownArticle content={book.description} />
          </DescriptionWrap>
        )}

        <SectionTitle>chapters</SectionTitle>

        {book.chapters.length === 0 ? (
          <EmptyState>
            {book.complete
              ? "this book has no published chapters."
              : "no chapters published yet. the first one is on its way."}
          </EmptyState>
        ) : (
          <ChapterList>
            {book.chapters.map((chapter, i) => (
              <ChapterRow key={chapter._id} to={`/blog/${chapter.slug}`}>
                <ChapterNumber>{chapter.chapterNumber ?? i + 1}</ChapterNumber>
                <ChapterBody>
                  <ChapterTitle>{chapter.title}</ChapterTitle>
                  {(chapter.subtitle || chapter.excerpt) && (
                    <ChapterExcerpt>{chapter.subtitle || chapter.excerpt}</ChapterExcerpt>
                  )}
                  <ChapterDate>
                    {new Date(chapter.publishedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </ChapterDate>
                </ChapterBody>
              </ChapterRow>
            ))}
          </ChapterList>
        )}

        {book.tags.length > 0 && (
          <TagList>
            {book.tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </TagList>
        )}

        <BottomNav>
          <NavLink to="/blog/books">&larr; all books</NavLink>
          {canEdit && <NavLink to={`/blog/manage/books/${book._id}`}>edit this book</NavLink>}
        </BottomNav>
      </Inner>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f0f0;
  padding: 20px;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const Inner = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const Centered = styled.div`
  text-align: center;
`;

const NavLink = styled(Link)`
  display: inline-block;
  color: #466a5d;
  text-decoration: none;
  font-size: 0.9rem;
  padding: 4px 0;

  &:hover {
    color: #2a4a3d;
    text-decoration: underline;
  }
`;

const BookHeader = styled.div`
  margin: 16px 0 20px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const KindTag = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6a4d8a;
  background: #f3edfa;
  padding: 2px 8px;
  border: 1px solid #d9c9ec;
`;

const CompleteTag = styled(KindTag)`
  color: #466a5d;
  background: #d4f9eb;
  border-color: #466a5d;
`;

const OngoingTag = styled(KindTag)`
  color: #b58b2a;
  background: #fdf1d3;
  border-color: #e0c37a;
`;

const MetaText = styled.span`
  font-size: 0.85rem;
  color: #888;
`;

const BookTitle = styled.h1`
  font-size: 2.4rem;
  color: #222;
  margin: 0 0 6px;
  line-height: 1.2;
  text-shadow: 1px 1px 3px #3c3c3c30;

  @media (max-width: 700px) {
    font-size: 1.8rem;
  }
`;

const BookSubtitle = styled.p`
  font-size: 1.1rem;
  color: #555;
  margin: 0;
  line-height: 1.4;
  font-style: italic;
`;

const Cover = styled.img`
  width: 100%;
  height: auto;
  border: 1px solid #ccc;
  margin-bottom: 20px;
`;

const DescriptionWrap = styled.div`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #666;
  margin: 0 0 12px;
`;

const ChapterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ChapterRow = styled(Link)`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: white;
  border: 1px solid #ccc;
  padding: 16px 18px;
  text-decoration: none;
  color: inherit;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.12);
  transition: all 0.15s;

  &:hover {
    box-shadow: 3px 5px rgba(0, 0, 0, 0.22);
    transform: translateY(-1px);
  }
`;

const ChapterNumber = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #cfe6dc;
  line-height: 1;
  min-width: 34px;
  font-variant-numeric: tabular-nums;
`;

const ChapterBody = styled.div`
  min-width: 0;
`;

const ChapterTitle = styled.h3`
  font-size: 1.1rem;
  color: #222;
  margin: 0 0 3px;
  line-height: 1.3;
`;

const ChapterExcerpt = styled.p`
  font-size: 0.88rem;
  color: #666;
  margin: 0 0 5px;
  line-height: 1.45;
`;

const ChapterDate = styled.span`
  font-size: 0.75rem;
  color: #999;
`;

const EmptyState = styled.p`
  color: #888;
  font-style: italic;
  padding: 20px 0;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
`;

const Tag = styled.span`
  font-size: 0.75rem;
  color: #777;
  background: #f5f5f5;
  padding: 3px 10px;
  border: 1px solid #ddd;
`;

const BottomNav = styled.div`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-top: 32px;
  padding-bottom: 40px;
`;

const Muted = styled.p`
  text-align: center;
  color: #888;
  padding: 60px 0;
`;

export default BookPage;
